import React, { useState, useRef, useEffect } from 'react';
import { Eye, ArrowRight, LogIn, Link } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const OneLinkLanding = () => {
  const [activeStatIndex, setActiveStatIndex] = useState(0);
  const statsRef = useRef(null);
  const navigate = useNavigate();

  const handleDemo = () => {
    navigate('/demo-preview');
  };

  const handleSignUp = () => {
    navigate('/signup');
  };

  const handleSignIn = () => {
    navigate('/signin');
  };

  const handleStatsScroll = () => {
    if (statsRef.current) {
      const scrollLeft = statsRef.current.scrollLeft;
      const cardWidth = statsRef.current.offsetWidth * 0.8; // 80% of container width
      const newIndex = Math.round(scrollLeft / cardWidth);
      setActiveStatIndex(newIndex);
    }
  };

  useEffect(() => {
    const statsContainer = statsRef.current;
    statsContainer?.addEventListener('scroll', handleStatsScroll);
    return () => {
      statsContainer?.removeEventListener('scroll', handleStatsScroll);
    };
  }, []);

  const renderDotIndicator = (count, activeIndex) => (
    <div className="flex justify-center gap-2 mt-6 sm:hidden mb-16">
      {[...Array(count)].map((_, index) => (
        <div
          key={index}
          className={`w-2 h-2 rounded-full transition-all duration-300 ${
            index === activeIndex ? 'bg-blue-400 w-4' : 'bg-slate-600'
          }`}
        />
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-900 text-white flex flex-col">
  

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="text-center max-w-4xl mx-auto w-full">
          {/* Glow Effect */}
          <div className="absolute -z-10 w-64 h-64 sm:w-96 sm:h-96 bg-blue-500/20 rounded-full blur-3xl"></div>
          
          <h1 className="text-3xl sm:text-5xl md:text-7xl lg:text-8xl font-light tracking-tight mb-6 sm:mb-8 px-2">
            Stop the Email<br />
            <span className="text-blue-400">Attachment Chaos</span>
          </h1>
          
          <p className="text-lg sm:text-xl md:text-2xl text-slate-300 mb-6 sm:mb-8 font-light max-w-3xl mx-auto leading-relaxed px-2">
            Give investors <strong className="text-white">one professional link</strong> instead of 
            scattered emails with pitch decks and documents.
          </p>
             {/* Link Preview */}
             <div className="p-4 max-w-sm mx-auto">
      <div className="bg-slate-900/80 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4 hover:border-blue-500/30 transition-all duration-300">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-violet-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <Link className="w-4 h-4 text-white" />
          </div>
          <div className="font-mono text-sm truncate">
            <span className="text-blue-400">decklink.co/p/</span>
            <span className="text-slate-300">yourcompany</span>
          </div>
        </div>
      </div>
    </div>

             {/* CTA Buttons */}
             <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16">
            <button
              onClick={handleDemo}
              className="w-full sm:w-auto px-12 py-6 bg-slate-800 hover:bg-slate-700 border border-slate-600 hover:border-blue-400 text-white hover:text-blue-400 rounded-xl text-xl font-medium transition-all flex items-center justify-center gap-3 group min-w-[280px]"
            >
              <Eye className="w-6 h-6" />
              View Live Demo
            </button>
            
            <button
              onClick={handleSignUp}
              className="w-full sm:w-auto px-12 py-6 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-xl text-xl font-medium transition-all flex items-center justify-center gap-3 group min-w-[280px]"
            >
              Get Started Free
              <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={handleSignIn}
              className="w-full sm:w-auto px-12 py-6 border border-slate-600 hover:border-blue-400 text-slate-300 hover:text-blue-400 bg-transparent rounded-xl text-xl font-medium transition-all flex items-center justify-center gap-3 group min-w-[280px]"
            >
              Sign In
            </button>
          </div>

       
    <div className="p-4">
      <div 
        className="flex overflow-x-auto snap-x snap-mandatory gap-4 pb-4 scrollbar-hide px-2 -mx-2 sm:px-0 sm:mx-0 sm:justify-center sm:gap-6 mb-4 sm:mb-8"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        <div className="flex-none w-[85vw] max-w-[285px] sm:w-auto sm:max-w-none bg-gradient-to-br from-blue-600/20 to-blue-800/20 backdrop-blur border border-blue-500/30 rounded-2xl p-8 sm:p-10 hover:border-blue-400/50 transition-all group snap-start relative overflow-hidden aspect-[3/4]">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 animate-pulse"></div>
          <div className="relative h-full flex flex-col justify-between">
            <div className="text-5xl sm:text-6xl md:text-7xl font-light text-blue-400 mb-6 animate-pulse">
              30%
            </div>
            <div>
              <div className="text-slate-300 font-medium mb-4 text-base sm:text-lg">Higher Response Rate</div>
              <div className="space-y-3 text-sm text-blue-300/70">
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-blue-400 rounded-full"></span>
                  <span>vs. email attachments</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-blue-400 rounded-full"></span>
                  <span>instant accessibility</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-blue-400 rounded-full"></span>
                  <span>mobile optimized</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex-none w-[85vw] max-w-[285px] sm:w-auto sm:max-w-none bg-gradient-to-br from-green-600/20 to-emerald-800/20 backdrop-blur border border-green-500/30 rounded-2xl p-8 sm:p-10 hover:border-green-400/50 transition-all group snap-start relative overflow-hidden aspect-[3/4]">
          <div className="absolute inset-0 bg-gradient-to-r from-green-500/5 to-emerald-500/5 animate-pulse" style={{ animationDelay: '0.5s' }}></div>
          <div className="relative h-full flex flex-col justify-between">
            <div className="text-5xl sm:text-6xl md:text-7xl font-light text-green-400 mb-6">
              15+
            </div>
            <div>
              <div className="text-slate-300 font-medium mb-4 text-base sm:text-lg">Founders Raised $1M+</div>
              <div className="space-y-3 text-sm text-green-300/70">
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-green-400 rounded-full"></span>
                  <span>using DeckLink</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-green-400 rounded-full"></span>
                  <span>Series A & B rounds</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-green-400 rounded-full"></span>
                  <span>VC backed startups</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex-none w-[85vw] max-w-[285px] sm:w-auto sm:max-w-none bg-gradient-to-br from-purple-600/20 to-violet-800/20 backdrop-blur border border-purple-500/30 rounded-2xl p-8 sm:p-10 hover:border-purple-400/50 transition-all group snap-start relative overflow-hidden aspect-[3/4]">
          <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 to-violet-500/5 animate-pulse" style={{ animationDelay: '1s' }}></div>
          <div className="relative h-full flex flex-col justify-between">
            <div className="text-5xl sm:text-6xl md:text-7xl font-light text-purple-400 mb-6">
              <div className="flex items-center gap-3">
                <span className="w-4 h-4 bg-purple-400 rounded-full animate-pulse"></span>
                <span className="w-3 h-3 bg-purple-400/70 rounded-full animate-pulse" style={{ animationDelay: '0.3s' }}></span>
                <span className="w-2 h-2 bg-purple-400/50 rounded-full animate-pulse" style={{ animationDelay: '0.6s' }}></span>
              </div>
            </div>
            <div>
              <div className="text-slate-300 font-medium mb-4 text-base sm:text-lg">Live Session Tracking</div>
              <div className="space-y-3 text-sm text-purple-300/70">
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-purple-400 rounded-full"></span>
                  <span>see who's viewing</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-purple-400 rounded-full"></span>
                  <span>real-time analytics</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-purple-400 rounded-full"></span>
                  <span>engagement insights</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
          {/* Pagination dots for mobile */}
          {renderDotIndicator(3, activeStatIndex)}
          {/* Simple Social Proof */}
          <p className="text-slate-500 text-sm mt-12 font-light">
            <strong className="text-slate-400">Investors love it.</strong> Over 10 founders have raised $1M+ using DeckLink.
          </p>
        </div>
      </div>
    </div>
  );
};

export default OneLinkLanding; 